import { Button, EditableText, Intent, Tooltip } from "@blueprintjs/core";
import { JitsiConference } from "lib/lib-jitsi-meet";
import { observable, reaction } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import { LocalUserState, persistSetting } from "../../common";

export interface NicknameInputProps {
    room: JitsiConference;
    localUser: LocalUserState;
}

interface NicknameInputState {
    hasConfirmed: boolean;
    value?: string;
    isValid: boolean;
    invalidMessage?: string;
}

// https://stackoverflow.com/a/12019115
// negative lookbehind is not supported in FF
// const ALLOWED_CHARS_REGEX = /^(?![_.])(?!.*[_.]{2})[a-zA-Z0-9._\s&]+(?<![_.])$/;
const ALLOWED_CHARS_REGEX = /^(?![_.])(?!.*[_.]{2})[a-zA-Z0-9._\s&]+$/;
const isValidNickname = (value: string) => {
    return value.length > 2 && value.length < 33 && ALLOWED_CHARS_REGEX.test(value);
};

function getDefaultInputState({ displayName }: LocalUserState): NicknameInputState {
    return {
        hasConfirmed: displayName !== undefined,
        value: displayName,
        isValid: displayName === undefined || isValidNickname(displayName),
    };
}

@observer
export default class extends React.PureComponent<NicknameInputProps, never> {
    public readonly displayName = "NicknameInput";

    @observable
    private input = getDefaultInputState(this.props.localUser);

    public componentDidMount() {
        const { room, localUser } = this.props;
        if (this.input.value !== undefined) {
            // broadcast to conference
            room.setDisplayName(this.input.value);
        }

        reaction(
            () => this.input.hasConfirmed,
            () => {
                const { hasConfirmed, value } = this.input;

                if (hasConfirmed && value !== undefined && value.trim() !== "") {
                    const trimmedValue = value.trim();
                    // HACKHACK awkward that we set localStorage item here, but read it in <DanceFloor>
                    persistSetting("displayName", trimmedValue);
                    localUser.displayName = trimmedValue;
                    room.setDisplayName(trimmedValue);
                }
            }
        );
    }

    public render() {
        const { hasConfirmed, value, isValid, invalidMessage } = this.input;
        const nameAndEditButton = (
            <>
                <EditableText
                    defaultValue={value}
                    placeholder="Choose a nickname"
                    isEditing={!hasConfirmed}
                    confirmOnEnterKey={true}
                    onChange={this.handleChange}
                    onConfirm={this.handleConfirm}
                    disabled={hasConfirmed}
                    minWidth={50}
                    intent={isValid ? undefined : Intent.DANGER}
                />
                <Button
                    icon={hasConfirmed ? "edit" : undefined}
                    disabled={!hasConfirmed}
                    minimal={true}
                    small={true}
                    onClick={this.toggleValueConfirmed}
                />
            </>
        );

        return (
            <Tooltip content={invalidMessage} intent={Intent.DANGER} disabled={isValid}>
                {nameAndEditButton}
            </Tooltip>
        );
    }

    private handleChange = (value: string) => {
        if (isValidNickname(value)) {
            this.input.value = value;
            this.input.isValid = true;
        } else {
            this.input.value = value;
            this.input.isValid = false;
            this.input.invalidMessage = ALLOWED_CHARS_REGEX.test(value)
                ? "Nickname must be 3-32 characters long"
                : "Nickname contains invalid characters";
        }
    };
    private handleConfirm = () => (this.input.hasConfirmed = true);
    private toggleValueConfirmed = () => (this.input.hasConfirmed = !this.input.hasConfirmed);
}
